/**
 * SimEvent → sound triggers (08 §4 tables) as a pure function, plus the
 * `AudioDirector` that owns per-entity loops and per-frame state sounds
 * (flight/return whir, shield hold, footfalls, last-10 ticks). DOM-free.
 */
import { DISC_THROW_SPEED, SHIELD_PERFECT_TICKS, SIM_HZ } from '../../../sim/constants.js'
import type { DiscStateName, MatchPhase, PlayerStateName, RoundEndReason, Team } from '../../../sim/types.js'
import type { SimEvent } from '../../../shared/events.js'
import { AUDIO_FLIGHT_RATE_BASE, AUDIO_FLIGHT_RATE_SPAN, AUDIO_STRIDE_M } from './constants.js'
import type { AudioEngine, Pos2 } from './engine.js'
import type { SoundId } from './manifest.js'

export interface AudioTrigger {
  id: SoundId
  pos?: Pos2
  entityKey?: string
  variation?: number
  delayS?: number
  /** Members of a group share one start time (double derez). */
  group?: string
  /** Stop this loop key (before playing `id`, if any). */
  stopLoop?: string
  /** Start a loop keyed by entity. */
  startLoop?: { key: string; id: SoundId; pos?: Pos2 }
  /** Master-FX side effects. */
  fx?: 'derezLocalTail'
  /** 08 §6: starts on the next beat boundary (with the stem change), not immediately. */
  onBeat?: boolean
}

export interface EventContext {
  localId: string | null
  localTeam: Team | null
  hub: boolean
  phase: MatchPhase
  posOf(playerId: string): Pos2 | undefined
  discPos(discId: number): Pos2 | undefined
  teamOf(playerId: string): Team | undefined
  /** Held disc count per player (for `disc_arm` on GO). */
  heldDiscs(): { playerId: string; count: number }[]
  /** Player ids alive at RESET (for staggered re-draw). */
  playerIds(): string[]
  /** Back edge of the listener zone (overtime floor loop anchor). */
  listenerBackEdge(): Pos2
  /** A `hit` event is in the same batch (the sting starts 120 ms later, 08 §9). */
  hitInBatch?: boolean
}

const LADDER_REASONS: RoundEndReason[] = ['ladder_round_elims', 'ladder_perfects', 'ladder_match_elims']

export function mapSimEvent(e: SimEvent, c: EventContext): AudioTrigger[] {
  const out: AudioTrigger[] = []
  const T = (t: AudioTrigger) => { out.push(t) }
  switch (e.type) {
    case 'throw': { const pos = { x: e.x, z: e.z }; T({ id: 'disc_throw', pos, startLoop: { key: `disc:${e.discId}`, id: 'disc_flight_loop', pos } }); break }
    case 'bounce': {
      const pos = { x: e.x, z: e.z }
      if (e.surface === 'wall') T({ id: 'disc_bounce_wall', pos })
      else if (e.surface === 'pillar') T({ id: 'disc_bounce_pillar', pos })
      else if (e.surface === 'corner') { T({ id: 'disc_bounce_wall', pos }); T({ id: 'disc_bounce_corner_tick', pos, delayS: 0.015 }) }
      // 'shield' is covered by deflectLate/deflectPerfect (no double).
      break
    }
    case 'clash': T({ id: 'disc_clash', pos: { x: e.x, z: e.z } }); break
    case 'hit': {
      const pos = c.posOf(e.victimId) ?? c.discPos(e.discId)
      T({ id: 'disc_hit_body', pos })
      if (e.bank) T({ id: 'hit_bank_tag', delayS: 0.08 })
      break
    }
    case 'deflectPerfect': T({ id: 'shield_deflect_perfect', pos: c.posOf(e.playerId) ?? c.discPos(e.discId) }); break
    case 'deflectLate': T({ id: 'shield_block_late', pos: c.posOf(e.playerId) ?? c.discPos(e.discId) }); break
    case 'catch': T({ id: 'disc_catch', pos: c.posOf(e.playerId), stopLoop: `disc:${e.discId}` }); break
    case 'ground': {
      const pos = { x: e.x, z: e.z }
      T({ id: 'disc_ground_drop', pos, stopLoop: `disc:${e.discId}` })
      if (e.reason === 'timeout') T({ id: 'return_lost_tick', pos, delayS: 0.05 })
      break
    }
    case 'pickup': {
      const pos = c.posOf(e.playerId)
      T({ id: 'disc_pickup', pos })
      if (e.stolen) T({ id: 'steal_tag', delayS: 0.06 })
      break
    }
    case 'rematerialise': T({ id: 'pen_redraw_short', pos: c.posOf(e.playerId) }); break
    case 'despawn': T({ id: 'pen_undraw_short', pos: c.discPos(e.discId), stopLoop: `disc:${e.discId}` }); break
    case 'dash': T({ id: 'dash', pos: c.posOf(e.playerId) }); break
    // S10 (08 §4b): take-off / touchdown; the director's grounded-flip fallback covers a skipped event.
    case 'jump': T({ id: 'jump', pos: c.posOf(e.playerId) }); break
    case 'land': T({ id: 'jump_land', pos: c.posOf(e.playerId) }); break
    case 'shieldRaise': { const pos = c.posOf(e.playerId); T({ id: 'shield_raise', pos, startLoop: { key: `player:${e.playerId}:shield`, id: 'shield_hold_loop', pos } }); break }
    case 'shieldLower': T({ id: 'shield_lower', pos: c.posOf(e.playerId), stopLoop: `player:${e.playerId}:shield` }); break
    case 'exhaust': T({ id: 'exhaust', pos: c.posOf(e.playerId), stopLoop: `player:${e.playerId}:shield` }); break
    case 'derez': {
      const pos = c.posOf(e.playerId)
      T({ id: 'derez_undraw', pos, group: `derez:${e.tick}`, stopLoop: `player:${e.playerId}:shield` })
      if (c.localId !== null && e.playerId === c.localId) T({ id: 'derez_local_tail', group: `derez:${e.tick}`, fx: 'derezLocalTail' })
      break
    }
    case 'respawn': T({ id: 'pen_redraw_full', pos: c.posOf(e.playerId) }); break
    case 'invulnEnd': T({ id: 'invuln_end', pos: c.posOf(e.playerId) }); break
    case 'phase': {
      if (e.phase === 'RESET') c.playerIds().forEach((id, i) => T({ id: 'pen_redraw_full', pos: c.posOf(id), delayS: i * 0.08 }))
      else if (e.phase === 'LOBBY_PICK') T({ id: 'ui_pick_open' })
      else if (e.phase === 'LOBBY_LOCK') T({ id: 'ui_lock_open' })
      else if (e.phase === 'REVEAL') T({ id: 'ui_reveal' })
      break
    }
    case 'countdown': {
      if (e.n === 0) { T({ id: 'count_go' }); for (const h of c.heldDiscs()) for (let i = 0; i < h.count; i++) T({ id: 'disc_arm', pos: c.posOf(h.playerId), delayS: i * 0.02 }) }
      else T({ id: 'count_tick', variation: 3 - e.n }) // 3→0, 2→1, 1→2: rising semitones
      break
    }
    // 08 §6: "`overtime_sting` on the beat, stems continue" — sting + floor loop share the boundary the OT stems land on.
    case 'overtime': { const pos = c.listenerBackEdge(); T({ id: 'overtime_sting', onBeat: true, startLoop: { key: 'overtime:floor', id: 'overtime_floor_undraw', pos } }); break }
    case 'boundsShrink': if (e.team === (c.localTeam ?? 0)) T({ id: 'overtime_step_tick', pos: { x: (e.xMin + e.xMax) / 2, z: e.team === 0 ? e.zMin : e.zMax } }); break
    case 'roundEnd': {
      const delay = c.hitInBatch ? 0.12 : 0
      T({ id: 'round_end_sting', delayS: delay, stopLoop: 'overtime:floor' })
      if (e.void) T({ id: 'void_round_sting', delayS: delay + 0.05 })
      else {
        if (LADDER_REASONS.includes(e.reason)) T({ id: 'ladder_tag', delayS: delay + 0.4 })
        // 08 §4: scoreboard bracket increments — one click per point (a round is one point).
        if (e.winner !== null) T({ id: 'ui_score_bump', delayS: delay + 0.3 })
        if (c.localTeam !== null && e.winner !== null) T({ id: e.winner === c.localTeam ? 'round_win_tag' : 'round_lose_tag', delayS: delay + 0.5 })
      }
      break
    }
    case 'coin': { [0, 0.22, 0.38].forEach((d) => T({ id: 'coin_tick', delayS: d })); T({ id: 'coin_land', delayS: 0.5 }); break }
    case 'matchEnd': {
      T({ id: 'match_end_sting', stopLoop: 'overtime:floor' })
      if (e.winner === null) T({ id: 'void_round_sting', delayS: 0.3 })
      else if (c.localTeam !== null) T({ id: e.winner === c.localTeam ? 'match_win_tag' : 'match_lose_tag', delayS: 1.0 })
      break
    }
    case 'rescind': break // 08 §9: no un-derez sound
    default: { const _x: never = e; void _x }
  }
  return out
}

/** Play a batch of triggers, honouring groups (one start time), loops and beat quantisation
 *  (`beatAt` = the next beat boundary from the music machine; `onBeat` triggers start there). */
export function fireTriggers(engine: AudioEngine, triggers: AudioTrigger[], beatAt?: number): void {
  const groups = new Map<string, number>()
  for (const t of triggers) {
    if (t.stopLoop) engine.stopLoop(t.stopLoop)
    let startAt: number | undefined
    if (t.group) { startAt = groups.get(t.group); if (startAt === undefined) { startAt = engine.ctx.currentTime; groups.set(t.group, startAt) } }
    if (t.onBeat && beatAt !== undefined) startAt = beatAt
    engine.play(t.id, { pos: t.pos, variation: t.variation, startDelayS: t.delayS, startAt: startAt !== undefined ? startAt + (t.delayS ?? 0) : undefined })
    if (t.fx === 'derezLocalTail') engine.derezLocalTail()
    if (t.startLoop) engine.startLoop(t.startLoop.key, t.startLoop.id, t.startLoop.pos, undefined, t.onBeat ? startAt : undefined)
  }
}

// ── per-frame director ──────────────────────────────────────────────────────

export interface WorldDisc { id: number; st: DiscStateName; x: number; z: number; speed: number; team: Team }
export interface WorldPlayer {
  id: string; st: PlayerStateName; x: number; z: number; alive: boolean; shieldAgeTicks?: number; team?: Team
  /** S10: grounded flag (PlayerSnap.g / PlayerState.grounded). Absent = unknown: no jump/land inference, footsteps as before. */
  g?: boolean
}
export interface WorldFrame {
  discs: WorldDisc[]
  players: WorldPlayer[]
  phase: MatchPhase
  mode: 'hub' | 'match'
  /** Seconds left on the LIVE timer (null outside LIVE). */
  timerS: number | null
  cameraTeam: Team
  /** Seconds since the phase started (LOBBY_LOCK metronome, 08 §4). */
  phaseS?: number
  /** Overtime bounds per team (bounds_rail, 08 §4). */
  bounds?: Record<Team, { xMin: number; xMax: number; zMin: number; zMax: number }>
}

export class AudioDirector {
  private discMissing = new Map<number, number>()
  private playerMissing = new Map<string, number>()
  private stride = new Map<string, { x: number; z: number; acc: number }>()
  private lastTimerTick = -1
  private lastLockTick = -1
  /** Previous per-player state (08 §4 transition sounds: →THROW_WINDUP, DASHING→, EXHAUST→). */
  private prevState = new Map<string, PlayerStateName>()
  /** S10: previous grounded flag per player — a flip fires `jump` / `jump_land` when the event was skipped (or not yet on the wire). */
  private prevGrounded = new Map<string, boolean>()
  /** S10: audio-clock time of the last `jump` / `land` trigger per player+kind — the event path and the grounded-flip
   *  fallback both pass through `jumpGate`, so whichever arrives first plays and the other (within 0.5 s) is dropped. */
  private lastJump = new Map<string, number>()
  /** Hub roster for hub_join / hub_leave; null until primed by the first frame after (re)entry. */
  private roster: Map<string, { x: number; z: number }> | null = null
  private prevBounds: Record<Team, { xMin: number; xMax: number; zMin: number; zMax: number }> | null = null
  /** Suppress one-shots for this many snapshots after a hard resync. */
  suppress = 0

  constructor(private readonly engine: AudioEngine) {}

  /** Reconnect/resync (08 §9): loops gone, one-shots suppressed for 2 snapshots. */
  hardResync(): void { this.engine.resetLoops(); this.suppress = 2; this.discMissing.clear(); this.playerMissing.clear(); this.stride.clear(); this.prevState.clear(); this.prevGrounded.clear(); this.lastJump.clear(); this.roster = null; this.prevBounds = null }

  /** S10 A08-J1: one `jump` / `jump_land` per take-off / touchdown whichever path (event or grounded flip) sees it first.
   *  Returns whether this trigger may play; a second request for the same player+kind within 0.5 s is refused. */
  jumpGate(playerId: string, kind: 'jump' | 'land'): boolean {
    const k = `${playerId}:${kind}`, now = this.engine.ctx.currentTime
    const last = this.lastJump.get(k)
    if (last !== undefined && now - last < 0.5) return false
    this.lastJump.set(k, now)
    return true
  }

  /** Called once per snapshot before its events are fired. Returns whether one-shots may play. */
  snapshot(): boolean { if (this.suppress > 0) { this.suppress--; return false } return true }

  frame(w: WorldFrame): void {
    const e = this.engine
    if (!e.unlocked) return
    // Disc loops by state (A08-08: stop the frame the state leaves).
    const seenDiscs = new Set<number>()
    for (const d of w.discs) {
      seenDiscs.add(d.id)
      this.discMissing.delete(d.id)
      const key = `disc:${d.id}`
      const pos = { x: d.x, z: d.z }
      const rate = AUDIO_FLIGHT_RATE_BASE + AUDIO_FLIGHT_RATE_SPAN * (d.speed / DISC_THROW_SPEED)
      if (d.st === 'FLIGHT') e.startLoop(key, 'disc_flight_loop', pos, rate)
      else if (d.st === 'RETURNING') e.startLoop(key, 'disc_return_loop', pos, rate)
      else if (e.hasLoop(key)) e.stopLoop(key)
    }
    // Entities absent two frames running → loop stopped.
    for (const v of e.live) {
      if (!v.entity || !v.loop) continue
      const m = /^disc:(\d+)$/.exec(v.entity)
      if (m && !seenDiscs.has(Number(m[1]))) { const n = (this.discMissing.get(Number(m[1])) ?? 0) + 1; this.discMissing.set(Number(m[1]), n); if (n >= 2) e.stopLoop(v.entity) }
    }
    const seenPlayers = new Set<string>()
    for (const p of w.players) {
      seenPlayers.add(p.id)
      this.playerMissing.delete(p.id)
      const key = `player:${p.id}:shield`
      const pos = { x: p.x, z: p.z }
      // 08 §4 state-transition one-shots (no SimEvent carries these).
      const prev = this.prevState.get(p.id)
      if (prev !== undefined && prev !== p.st && p.alive) {
        if (p.st === 'THROW_WINDUP') e.play('throw_windup', { pos })
        else if (prev === 'DASHING') e.play('dash_land', { pos })
        else if (prev === 'EXHAUST') e.play('exhaust_recover', { pos })
      }
      this.prevState.set(p.id, p.st)
      // S10 A08-J1: grounded flip → take-off / touchdown one-shots (once per flip; the event path is primary and marks
      // the player so a flip seen in the same snapshot does not double it).
      if (p.g !== undefined) {
        const pg = this.prevGrounded.get(p.id)
        if (pg !== undefined && pg !== p.g && p.alive) {
          const kind = p.g ? 'land' : 'jump'
          if (this.jumpGate(p.id, kind)) e.play(kind === 'jump' ? 'jump' : 'jump_land', { pos })
        }
        this.prevGrounded.set(p.id, p.g)
      }
      // 08 §4 bounds_rail: pushed by a SHRINKING bound (bound moved this frame and the player sits on it).
      if (w.phase === 'OVERTIME' && w.bounds && p.team !== undefined && p.alive && this.prevBounds) {
        const b = w.bounds[p.team], pb = this.prevBounds[p.team]
        const moved = b.xMin !== pb.xMin || b.xMax !== pb.xMax || b.zMin !== pb.zMin || b.zMax !== pb.zMax
        const onRail = p.x <= b.xMin + 0.05 || p.x >= b.xMax - 0.05 || p.z <= b.zMin + 0.05 || p.z >= b.zMax - 0.05
        if (moved && onRail) e.play('bounds_rail', { pos })
      }
      if (p.st === 'SHIELD' && p.alive) {
        e.startLoop(key, 'shield_hold_loop', pos)
        // 08 §4b: brighter "ready" layer for the perfect window.
        e.setLoopGain(key, (p.shieldAgeTicks ?? 99) <= SHIELD_PERFECT_TICKS ? 1.6 : 1)
      } else if (e.hasLoop(key)) e.stopLoop(key)
      // Footfalls every AUDIO_STRIDE_M while moving on foot.
      const s = this.stride.get(p.id) ?? { x: p.x, z: p.z, acc: 0 }
      const moved = Math.hypot(p.x - s.x, p.z - s.z)
      s.x = p.x; s.z = p.z
      // S10: no footfalls while airborne (`g === false`); the stride accumulator restarts on touchdown.
      if (p.alive && p.g !== false && (p.st === 'IDLE' || p.st === 'THROW_WINDUP' || p.st === 'THROW_RECOVERY' || p.st === 'EXHAUST') && moved < 0.5) {
        s.acc += moved
        if (s.acc >= AUDIO_STRIDE_M) { s.acc -= AUDIO_STRIDE_M; e.play('step_concrete', { pos }) }
      } else s.acc = 0
      this.stride.set(p.id, s)
    }
    this.prevBounds = w.bounds ? { 0: { ...w.bounds[0] }, 1: { ...w.bounds[1] } } : null
    for (const id of [...this.prevState.keys()]) if (!seenPlayers.has(id)) { this.prevState.delete(id); this.prevGrounded.delete(id) }
    // Hub roster: join/leave ticks at the player's position (08 §4); primed silently on entry.
    if (w.mode === 'hub') {
      const next = new Map(w.players.map((p) => [p.id, { x: p.x, z: p.z }] as const))
      if (this.roster) {
        for (const [id, pos] of next) if (!this.roster.has(id)) e.play('hub_join', { pos })
        for (const [id, pos] of this.roster) if (!next.has(id)) e.play('hub_leave', { pos })
      }
      this.roster = next
    } else this.roster = null
    // LOBBY_LOCK metronome: once per second for 10 s, last 3 brighter (08 §4 `ui_lock_tick`).
    if (w.phase === 'LOBBY_LOCK' && w.phaseS !== undefined && w.phaseS < 10) {
      const sec = Math.floor(w.phaseS)
      if (sec !== this.lastLockTick) { this.lastLockTick = sec; e.play('ui_lock_tick', { variation: sec >= 7 ? 1 : 0 }) }
    } else this.lastLockTick = -1
    for (const v of e.live) {
      if (!v.entity || !v.loop) continue
      const m = /^player:(.+):shield$/.exec(v.entity)
      if (m && !seenPlayers.has(m[1])) { const n = (this.playerMissing.get(m[1]) ?? 0) + 1; this.playerMissing.set(m[1], n); if (n >= 2) e.stopLoop(v.entity) }
    }
    // Timer ≤ 10 s: one tick per second, last 3 brighter (variation 1).
    if (w.phase === 'LIVE' && w.timerS !== null && w.timerS <= 10 && w.timerS > 0) {
      const sec = Math.ceil(w.timerS)
      if (sec !== this.lastTimerTick) { this.lastTimerTick = sec; e.play('timer_last10_tick', { variation: sec <= 3 ? 1 : 0 }) }
    } else this.lastTimerTick = -1
    if (w.phase !== 'OVERTIME' && e.hasLoop('overtime:floor')) e.stopLoop('overtime:floor')
  }
}

export const ticksToS = (t: number): number => t / SIM_HZ
